import { useState, useMemo, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../ui/card";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../ui/tabs";
import { Calendar } from "../../ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell, Treemap } from "recharts";
import { Calendar as CalendarIcon, MousePointerClick, QrCode, TrendingUp, Globe, MapPin, ExternalLink, Laptop2, TabletSmartphoneIcon, Ban } from "lucide-react";
import { Progress } from "../../ui/progress";
import { format, subDays, subMonths } from "date-fns";
import { useAuth } from "../../contexts/AuthContext";
import config from "../../utils/config";
import { Heatmap } from "./Heatmap";

interface AnalyticsData {
  date: string;
  directClicks: number;
  qrScans: number;
  total: number;
}

interface LocationData {
  name: string;
  clicks: number;
  percentage: number;
  [key: string]: string | number;
}

interface TimelineDataPoint {
  date: string;
  clicks: number;
  qrScans: number;
  directVisits: number;
}

interface AnalyticsSummary {
  totalClicks: number;
  deviceTypes: Record<string, number>;
  operatingSystems: Record<string, number>;
  browsers: Record<string, number>;
  countries: Record<string, number>;
  cities: Record<string, number>;
  referrers: Record<string, number>;
  qrCodeScans: number;
  directVisits: number;
  deactivatedClicks: number;  // FR-25: Count of deactivated/blocked redirects
  timeline: TimelineDataPoint[];
  accessTimestamps: string[];
}

// Colors for pie charts
const COLORS = ["#3b82f6", "#8b5cf6", "#ec4899", "#f59e0b", "#10b981", "#6366f1", "#14b8a6", "#f97316", "#06b6d4", "#84cc16"];

type DateRange = "24h" | "7d" | "30d" | "6m" | "custom";
type DeactivationFilter = "all" | "activeOnly" | "deactivatedOnly";  // FR-25

export function AnalyticsPage({ onBack, urlMappingId }: { onBack: () => void; urlMappingId: number }) {
  const { token } = useAuth();
  const [dateRange, setDateRange] = useState<DateRange>("7d");
  const [customDateRange, setCustomDateRange] = useState<{ from: Date | undefined; to: Date | undefined }>({
    from: undefined,
    to: undefined,
  });
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [deactivationFilter, setDeactivationFilter] = useState<DeactivationFilter>("activeOnly");  // FR-25
  const [analytics, setAnalytics] = useState<AnalyticsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { startDate, endDate } = useMemo(() => {
    const end = new Date();
    let start = new Date();

    if (dateRange === "custom" && customDateRange.from && customDateRange.to) {
      return { startDate: customDateRange.from, endDate: customDateRange.to };
    }

    switch (dateRange) {
      case "24h":
        start = subDays(end, 1);
        break;
      case "7d":
        start = subDays(end, 7);
        break;
      case "30d":
        start = subDays(end, 30);
        break;
      case "6m":
        start = subMonths(end, 6);
        break;
    }

    return { startDate: start, endDate: end };
  }, [dateRange, customDateRange]);

  useEffect(() => {
    fetchAnalytics();
  }, [dateRange, customDateRange, urlMappingId, startDate, endDate, deactivationFilter]);  // FR-25: Added deactivationFilter

  const fetchAnalytics = async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      params.append('startDate', startDate.toISOString());
      params.append('endDate', endDate.toISOString());

      // FR-25: Add deactivation filter parameter
      if (deactivationFilter === "all") {
        params.append('deactivationFilter', '0');  // All
      } else if (deactivationFilter === "activeOnly") {
        params.append('deactivationFilter', '1');  // ActiveOnly
      } else if (deactivationFilter === "deactivatedOnly") {
        params.append('deactivationFilter', '2');  // DeactivatedOnly
      }

      const url = `${config.analyticsServiceUrl}/api/analytics/${urlMappingId}?${params.toString()}`;
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('No analytics data found for this URL');
        }
        throw new Error('Failed to fetch analytics');
      }

      const data = await response.json();
      setAnalytics(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while fetching analytics');
    } finally {
      setLoading(false);
    }
  };

  // Transform timeline data to chart format
  const analyticsData = useMemo(() => {
    if (!analytics?.timeline) return [];
    return analytics.timeline.map((point) => ({
      date: format(new Date(point.date), "MMM dd"),
      directClicks: point.directVisits,
      qrScans: point.qrScans,
      total: point.clicks,
    }));
  }, [analytics]);

  const totalClicks = analytics?.totalClicks || 0;
  const totalDirectClicks = analytics?.directVisits || 0;
  const totalQrScans = analytics?.qrCodeScans || 0;
  const totalDeactivated = analytics?.deactivatedClicks || 0;  // FR-25
  const directPercentage = totalClicks > 0 ? ((totalDirectClicks / totalClicks) * 100).toFixed(1) : '0';
  const qrPercentage = totalClicks > 0 ? ((totalQrScans / totalClicks) * 100).toFixed(1) : '0';

  const countryData = useMemo(() => {
    if (!analytics?.countries) return [];
    return Object.entries(analytics.countries)
      .map(([name, clicks]) => ({
        name,
        clicks,
        percentage: totalClicks > 0 ? parseFloat(((clicks / totalClicks) * 100).toFixed(1)) : 0,
      }))
      .sort((a, b) => b.clicks - a.clicks)
      .slice(0, 8);
  }, [analytics, totalClicks]);

  const cityData = useMemo(() => {
    if (!analytics?.cities) return [];
    return Object.entries(analytics.cities)
      .map(([name, clicks]) => ({
        name,
        clicks,
        percentage: totalClicks > 0 ? parseFloat(((clicks / totalClicks) * 100).toFixed(1)) : 0,
      }))
      .sort((a, b) => b.clicks - a.clicks)
      .slice(0, 10);
  }, [analytics, totalClicks]);


  const osData = useMemo(() => {
    if (!analytics?.operatingSystems) return [];
    return Object.entries(analytics.operatingSystems)
      .map(([name, clicks]) => ({
        name,
        clicks,
        percentage: totalClicks > 0 ? parseFloat(((clicks / totalClicks) * 100).toFixed(1)) : 0,
      }))
      .sort((a, b) => b.clicks - a.clicks)
      .slice(0, 10);
  }, [analytics, totalClicks]);

  const deviceData = useMemo(() => {
    if (!analytics?.deviceTypes) return [];
    return Object.entries(analytics.deviceTypes)
      .map(([name, clicks]) => ({
        name,
        clicks,
        percentage: totalClicks > 0 ? parseFloat(((clicks / totalClicks) * 100).toFixed(1)) : 0,
      }))
      .sort((a, b) => b.clicks - a.clicks)
      .slice(0, 10);
  }, [analytics, totalClicks]);

  const browserData = useMemo(() => {
    if (!analytics?.browsers) return [];
    return Object.entries(analytics.browsers)
      .map(([name, clicks]) => ({
        name,
        clicks,
        percentage: totalClicks > 0 ? parseFloat(((clicks / totalClicks) * 100).toFixed(1)) : 0,
      }))
      .sort((a, b) => b.clicks - a.clicks)
      .slice(0, 10);
  }, [analytics, totalClicks]);

  const referrerData = useMemo(() => {
    if (!analytics?.referrers) return [];
    return Object.entries(analytics.referrers)
      .map(([name, clicks]) => ({
        name,
        clicks,
        percentage: totalClicks > 0 ? parseFloat(((clicks / totalClicks) * 100).toFixed(1)) : 0,
      }))
      .sort((a, b) => b.clicks - a.clicks)
      .slice(0, 10);
  }, [analytics, totalClicks]);

  // TreeMap data for all referrers (not limited to top 10)
  const referrerTreeMapData = useMemo(() => {
    if (!analytics?.referrers) return [];
    return Object.entries(analytics.referrers)
      .map(([name, clicks]) => ({
        name,
        size: clicks,
        percentage: totalClicks > 0 ? parseFloat(((clicks / totalClicks) * 100).toFixed(1)) : 0,
      }))
      .sort((a, b) => b.size - a.size);
  }, [analytics, totalClicks]);




  return (
    <div className="h-full w-full flex flex-col bg-background">
      {/* Header */}
      <div className="border-b bg-background px-6 py-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <Button variant="ghost" onClick={onBack} className="mb-2">
              ← Back to URLs
            </Button>
            <h1 className="text-2xl font-bold">Click Analytics</h1>
            <p className="text-sm text-muted-foreground">
              Analyze your URL performance and traffic patterns
            </p>
          </div>
        </div>

        {/* Date Range Selector */}
        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            variant={dateRange === "24h" ? "default" : "outline"}
            onClick={() => setDateRange("24h")}
          >
            Last 24h
          </Button>
          <Button
            size="sm"
            variant={dateRange === "7d" ? "default" : "outline"}
            onClick={() => setDateRange("7d")}
          >
            Last 7 days
          </Button>
          <Button
            size="sm"
            variant={dateRange === "30d" ? "default" : "outline"}
            onClick={() => setDateRange("30d")}
          >
            Last 30 days
          </Button>
          <Button
            size="sm"
            variant={dateRange === "6m" ? "default" : "outline"}
            onClick={() => setDateRange("6m")}
          >
            Last 6 months
          </Button>
          <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
            <PopoverTrigger asChild>
              <Button size="sm" variant={dateRange === "custom" ? "default" : "outline"}>
                <CalendarIcon className="size-4 mr-2" />
                Custom Range
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="range"
                selected={{
                  from: customDateRange.from,
                  to: customDateRange.to,
                }}
                onSelect={(range) => {
                  setCustomDateRange({ from: range?.from, to: range?.to });
                  if (range?.from && range?.to) {
                    setDateRange("custom");
                    setCalendarOpen(false);
                  }
                }}
                numberOfMonths={2}
              />
            </PopoverContent>
          </Popover>
          {dateRange === "custom" && customDateRange.from && customDateRange.to && (
            <span className="text-sm text-muted-foreground">
              {format(customDateRange.from, "MMM dd, yyyy")} - {format(customDateRange.to, "MMM dd, yyyy")}
            </span>
          )}
        </div>

        {/* FR-25: Deactivation Filter */}
        <div className="flex flex-wrap items-center gap-2 mt-3">
          <span className="text-sm font-medium text-muted-foreground">Filter:</span>
          <Button
            size="sm"
            variant={deactivationFilter === "activeOnly" ? "default" : "outline"}
            onClick={() => setDeactivationFilter("activeOnly")}
          >
            Active Only
          </Button>
          <Button
            size="sm"
            variant={deactivationFilter === "all" ? "default" : "outline"}
            onClick={() => setDeactivationFilter("all")}
          >
            All Clicks
          </Button>
          <Button
            size="sm"
            variant={deactivationFilter === "deactivatedOnly" ? "default" : "outline"}
            onClick={() => setDeactivationFilter("deactivatedOnly")}
          >
            Deactivated Only
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6 space-y-6">
        {loading && (
          <div className="flex items-center justify-center h-64">
            <p className="text-muted-foreground">Loading analytics...</p>
          </div>
        )}

        {error && (
          <div className="flex items-center justify-center h-64">
            <Card className="border-destructive">
              <CardContent className="pt-6">
                <p className="text-destructive text-center">{error}</p>
              </CardContent>
            </Card>
          </div>
        )}

        {!loading && !error && analytics && (
          <>
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Clicks</CardTitle>
              <TrendingUp className="size-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalClicks.toLocaleString()}</div>
              <p className="text-xs text-muted-foreground">
                {format(startDate, "MMM dd")} - {format(endDate, "MMM dd")}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Direct Clicks</CardTitle>
              <MousePointerClick className="size-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalDirectClicks.toLocaleString()}</div>
              <div className="flex items-center gap-2">
                <Badge variant="secondary">{directPercentage}%</Badge>
                <p className="text-xs text-muted-foreground">of total traffic</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">QR Scans</CardTitle>
              <QrCode className="size-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalQrScans.toLocaleString()}</div>
              <div className="flex items-center gap-2">
                <Badge variant="secondary">{qrPercentage}%</Badge>
                <p className="text-xs text-muted-foreground">of total traffic</p>
              </div>
            </CardContent>
          </Card>

          {/* FR-25: Deactivated Clicks Card */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Deactivated Clicks</CardTitle>
              <Ban className="size-4 text-destructive" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalDeactivated.toLocaleString()}</div>
              <p className="text-xs text-muted-foreground">
                Blocked or inactive redirects
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Area Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Traffic Over Time</CardTitle>
            <CardDescription>
              Comparing direct clicks and QR scans throughout the selected period
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={350}>
              <AreaChart data={analyticsData}>
                <defs>
                  <linearGradient id="colorDirect" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorQR" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis
                  dataKey="date"
                  className="text-xs"
                  tick={{ fill: "currentColor" }}
                  tickLine={{ stroke: "currentColor" }}
                />
                <YAxis
                  className="text-xs"
                  tick={{ fill: "currentColor" }}
                  tickLine={{ stroke: "currentColor" }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "hsl(var(--background))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: "6px",
                  }}
                />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="directClicks"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorDirect)"
                  name="Direct Clicks"
                />
                <Area
                  type="monotone"
                  dataKey="qrScans"
                  stroke="#8b5cf6"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorQR)"
                  name="QR Scans"
                />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Geographic Analytics */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Country Distribution */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="size-5" />
                Clicks by Country
              </CardTitle>
              <CardDescription>Geographic distribution of your traffic</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col lg:flex-row items-center gap-6">
                {/* Pie Chart */}
                <div className="w-full lg:w-1/2 flex justify-center">
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie
                        data={countryData}
                        innerRadius="40%"
                        outerRadius="100%"
                        labelLine={false}
                        fill="#8884d8"
                        dataKey="clicks"
                      >
                        {countryData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "hsl(var(--background))",
                          border: "1px solid hsl(var(--border))",
                          borderRadius: "6px",
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                {/* Legend/List */}
                <div className="w-full lg:w-1/2 space-y-2">
                  {countryData.slice(0, 6).map((country, index) => (
                    <div key={country.name} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: COLORS[index % COLORS.length] }}
                        />
                        <span className="text-sm">{country.name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">{country.clicks.toLocaleString()}</span>
                        <Badge variant="secondary" className="text-xs">
                          {country.percentage}%
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* City Distribution */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="size-5" />
                Clicks by City
              </CardTitle>
              <CardDescription>Top cities driving your traffic</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col lg:flex-row items-center gap-6">
                {/* Pie Chart */}
                <div className="w-full lg:w-1/2 flex justify-center">
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie
                        data={cityData}
                        innerRadius="40%"
                        outerRadius="100%"
                        labelLine={false}
                        fill="#8884d8"
                        dataKey="clicks"
                      >
                        {cityData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "hsl(var(--background))",
                          border: "1px solid hsl(var(--border))",
                          borderRadius: "6px",
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                {/* Legend/List */}
                <div className="w-full lg:w-1/2 space-y-2">
                  {cityData.slice(0, 6).map((city, index) => (
                    <div key={city.name} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: COLORS[index % COLORS.length] }}
                        />
                        <span className="text-sm">{city.name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">{city.clicks.toLocaleString()}</span>
                        <Badge variant="secondary" className="text-xs">
                          {city.percentage}%
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Heatmap */}
        {analytics.accessTimestamps && analytics.accessTimestamps.length > 0 && (
          <Heatmap accessTimestamps={analytics.accessTimestamps} />
        )}

         {/* Device Analytics */}
         <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Operating System */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Laptop2 className="size-5" />
                 Operating Systems
              </CardTitle>
              <CardDescription>Operating System distribution of your traffic</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col lg:flex-row items-center gap-6">
                {/* Pie Chart */}
                <div className="w-full lg:w-1/2 flex justify-center">
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie
                        data={osData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="clicks"
                      >
                        {osData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "hsl(var(--background))",
                          border: "1px solid hsl(var(--border))",
                          borderRadius: "6px",
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                {/* Legend/List */}
                <div className="w-full lg:w-1/2 space-y-2">
                  {osData.slice(0, 6).map((operatingSystem, index) => (
                    <div key={operatingSystem.name} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: COLORS[index % COLORS.length] }}
                        />
                        <span className="text-sm">{operatingSystem.name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">{operatingSystem.clicks.toLocaleString()}</span>
                        <Badge variant="secondary" className="text-xs">
                          {operatingSystem.percentage}%
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Device Types */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TabletSmartphoneIcon className="size-5" />
                 Device Type
              </CardTitle>
              <CardDescription>Traffic based on end user device type</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col lg:flex-row items-center gap-6">
                {/* Pie Chart */}
                <div className="w-full lg:w-1/2 flex justify-center">
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie
                        data={deviceData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="clicks"
                      >
                        {deviceData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "hsl(var(--background))",
                          border: "1px solid hsl(var(--border))",
                          borderRadius: "6px",
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                {/* Legend/List */}
                <div className="w-full lg:w-1/2 space-y-2">
                  {deviceData.slice(0, 6).map((device, index) => (
                    <div key={device.name} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: COLORS[index % COLORS.length] }}
                        />
                        <span className="text-sm">{device.name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">{device.clicks.toLocaleString()}</span>
                        <Badge variant="secondary" className="text-xs">
                          {device.percentage}%
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Operating System */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="size-5" />
                 Browsers
              </CardTitle>
              <CardDescription>Internet Browsers driving your traffic</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col lg:flex-row items-center gap-6">
                {/* Pie Chart */}
                <div className="w-full lg:w-1/2 flex justify-center">
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie
                        data={browserData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="clicks"
                      >
                        {browserData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "hsl(var(--background))",
                          border: "1px solid hsl(var(--border))",
                          borderRadius: "6px",
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                {/* Legend/List */}
                <div className="w-full lg:w-1/2 space-y-2">
                  {browserData.slice(0, 6).map((browser, index) => (
                    <div key={browser.name} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: COLORS[index % COLORS.length] }}
                        />
                        <span className="text-sm">{browser.name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">{browser.clicks.toLocaleString()}</span>
                        <Badge variant="secondary" className="text-xs">
                          {browser.percentage}%
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Referrer TreeMap */}
        {referrerTreeMapData.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ExternalLink className="size-5" />
                Referrer Distribution (TreeMap)
              </CardTitle>
              <CardDescription>Visual representation of all referrer sources</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={400}>
                <Treemap
                  data={referrerTreeMapData}
                  dataKey="size"
                  stroke="#fff"
                  fill="#8884d8"
                  content={<CustomTreeMapContent />}
                >
                  <Tooltip
                    content={({ payload }) => {
                      if (!payload || !payload[0]) return null;
                      const data = payload[0].payload;
                      return (
                        <div className="bg-background border border-border rounded-md p-2 shadow-lg">
                          <p className="font-semibold">{data.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {data.size.toLocaleString()} clicks ({data.percentage}%)
                          </p>
                        </div>
                      );
                    }}
                  />
                </Treemap>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Referrer Table with Progress Bars */}
        {referrerData.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ExternalLink className="size-5" />
                Referrer Analytics Table
              </CardTitle>
              <CardDescription>Detailed breakdown of referrer sources with traffic share</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Table Header */}
                <div className="grid grid-cols-12 gap-4 pb-2 border-b font-semibold text-sm text-muted-foreground">
                  <div className="col-span-5">Referrer</div>
                  <div className="col-span-2 text-right">Clicks</div>
                  <div className="col-span-5">Share</div>
                </div>

                {/* Table Rows */}
                {referrerData.map((referrer, index) => (
                  <div key={referrer.name} className="grid grid-cols-12 gap-4 items-center py-2">
                    {/* Referrer Name */}
                    <div className="col-span-5 flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                      />
                      <span className="text-sm font-medium truncate" title={referrer.name}>
                        {referrer.name}
                      </span>
                    </div>

                    {/* Clicks */}
                    <div className="col-span-2 text-right font-semibold text-sm">
                      {referrer.clicks.toLocaleString()}
                    </div>

                    {/* Share Progress Bar */}
                    <div className="col-span-5 flex items-center gap-3">
                      <Progress
                        value={referrer.percentage}
                        className="flex-1"
                        style={{
                          //@ts-ignore
                          '--progress-background': COLORS[index % COLORS.length]
                        }}
                      />
                      <Badge variant="secondary" className="text-xs min-w-[50px] justify-center">
                        {referrer.percentage}%
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
        </>
        )}
      </div>
    </div>
  );
}

// Custom TreeMap Content Component
const CustomTreeMapContent = (props: any) => {
  const { x, y, width, height, index, name, value, root } = props;

  // Don't render if too small or missing data
  if (!width || !height || width < 30 || height < 30 || !name) return null;

  const COLORS_MAP = ["#3b82f6", "#8b5cf6", "#ec4899", "#f59e0b", "#10b981", "#6366f1", "#14b8a6", "#f97316", "#06b6d4", "#84cc16"];

  // Calculate percentage from root
  const totalValue = root?.children?.reduce((sum: number, child: any) => sum + (child.size || 0), 0) || 1;
  const percentage = ((value / totalValue) * 100).toFixed(1);

  return (
    <g>
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        style={{
          fill: COLORS_MAP[(index || 0) % COLORS_MAP.length],
          stroke: '#fff',
          strokeWidth: 2,
          opacity: 0.9,
        }}
      />
      {width > 60 && height > 40 && (
        <>
          <text
            x={x + width / 2}
            y={y + height / 2 - 8}
            textAnchor="middle"
            fill="#fff"
            fontSize={12}
            fontWeight="bold"
          >
            {name && name.length > 15 ? name.substring(0, 15) + '...' : name}
          </text>
          <text
            x={x + width / 2}
            y={y + height / 2 + 8}
            textAnchor="middle"
            fill="#fff"
            fontSize={10}
          >
            {value ? value.toLocaleString() : '0'} ({percentage}%)
          </text>
        </>
      )}
    </g>
  );
};
